/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { DEPARTMENTS, type Department, type StaffMember, type Task } from "../shared/types";

type Props = { task: Task; staff: StaffMember[]; onClose: (changed: boolean) => void };

export const TaskEditor = defineComponent<Props>({
  name: "TaskEditor",
  props: ["task", "staff", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const title = ref(props.task.title);
    const notes = ref(props.task.notes ?? "");
    const due = ref(props.task.due_date ?? "");
    const assignee = ref<number | null>(props.task.assigned_to);
    const department = ref<string>(props.task.department ?? "");
    const high = ref(props.task.priority === "high");
    const saving = ref(false);

    const run = async (action: () => Promise<unknown>) => {
      saving.value = true;
      try {
        await action();
        props.onClose(true);
      } catch {
        toast.showToast(t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const save = () =>
      run(() =>
        trpc.workday.tasks.update.mutate({
          id: props.task.id,
          title: title.value.trim(),
          notes: notes.value.trim() || null,
          due_date: due.value || null,
          assigned_to: assignee.value,
          department: (department.value || null) as Department | null,
          priority: high.value ? "high" : "normal",
        }),
      );

    const remove = () => {
      if (confirm(t("workday.tasks.delete_confirm"))) run(() => trpc.workday.tasks.delete.mutate({ id: props.task.id }));
    };

    return () => (
      <LemmonModal
        title={t("workday.tasks.edit_title")}
        onClose={() => props.onClose(false)}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-ghost text-error" disabled={saving.value} onClick={remove}>
                {t("workday.common.delete")}
              </button>
              <div class="flex gap-2">
                <button class="btn btn-neutral" onClick={() => props.onClose(false)}>
                  {t("general.words.cancel")}
                </button>
                <button class="btn btn-primary" disabled={saving.value || !title.value.trim()} onClick={save}>
                  {t("workday.common.save")}
                </button>
              </div>
            </div>
          </div>
        )}
      >
        <div class="space-y-4 p-6">
          <input class="input input-bordered w-full text-lg font-medium" v-model={title.value} />
          <textarea class="textarea textarea-bordered w-full text-sm" rows={3} placeholder={t("workday.tasks.notes_placeholder")} v-model={notes.value} />
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label class="block">
              <span class="mb-1 block text-sm font-medium">{t("workday.tasks.due")}</span>
              <input type="date" class="input input-bordered input-sm w-full" v-model={due.value} />
            </label>
            <label class="block">
              <span class="mb-1 block text-sm font-medium">{t("workday.tasks.assignee")}</span>
              <select
                class="select select-bordered select-sm w-full"
                value={assignee.value ?? ""}
                onChange={(e: Event) => {
                  const v = (e.target as HTMLSelectElement).value;
                  assignee.value = v ? Number(v) : null;
                }}
              >
                <option value="">{t("workday.tasks.anyone")}</option>
                {props.staff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label class="block">
              <span class="mb-1 block text-sm font-medium">{t("workday.common.department_label")}</span>
              <select class="select select-bordered select-sm w-full" value={department.value} onChange={(e: Event) => (department.value = (e.target as HTMLSelectElement).value)}>
                <option value="">—</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>
                    {t(`workday.department.${d}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label class="flex items-center gap-2 text-sm">
            <input type="checkbox" class="checkbox checkbox-sm checkbox-error" v-model={high.value} />
            {t("workday.tasks.high_priority")}
          </label>
        </div>
      </LemmonModal>
    );
  },
});
